import { API } from '../constants/constants';
import { AssistantChartPayload } from '../../components/ChippyPage/chartPayloadAdapter';

export type ChippyMessageRole = 'assistant' | 'user' | 'system';
export type ChippyMessageFeedbackSentiment = 'up' | 'down';

export interface ChippyMessageFeedback {
  sentiment: ChippyMessageFeedbackSentiment;
  comment?: string;
  updatedAt?: string;
}

export interface ChippyThread {
  _id: string;
  title: string;
  firstMessagePreview?: string;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  updatedAt?: string;
  createdAt?: string;
  messageCount?: number;
  sessionState?: Record<string, unknown> | null;
}

export interface ChippyMessageRecord {
  _id: string;
  role: ChippyMessageRole;
  text: string;
  chart?: AssistantChartPayload | null;
  feedback?: ChippyMessageFeedback | null;
  createdAt?: string;
}

interface GetChippyThreadsResponse {
  success: boolean;
  data: ChippyThread[];
}

interface GetChippyMessagesResponse {
  success: boolean;
  data: ChippyMessageRecord[];
  thread?: ChippyThread;
}

interface CreateChippyThreadResponse {
  success: boolean;
  thread: ChippyThread;
  initialMessage?: ChippyMessageRecord | null;
}

interface AppendChippyMessageResponse {
  success: boolean;
  message?: ChippyMessageRecord;
  thread?: ChippyThread;
}

interface UpdateChippyMessageFeedbackResponse {
  success: boolean;
  data: ChippyMessageRecord;
}

export const getChippyThreads = async (limit = 50) => {
  const { data } = await API.get<GetChippyThreadsResponse>('/chippy/threads', { params: { limit } });
  return data.data ?? [];
};

export const getChippyMessagesByThreadId = async (threadId: string, limit = 500) => {
  const { data } = await API.get<GetChippyMessagesResponse>(`/chippy/thread/${threadId}/messages`, {
    params: { limit },
  });
  return {
    messages: data.data ?? [],
    thread: data.thread ?? null,
  };
};

export const createChippyThread = async (payload: {
  initialMessage?: string;
  title?: string;
  sessionState?: Record<string, unknown> | null;
}) => {
  const { data } = await API.post<CreateChippyThreadResponse>('/chippy/thread', payload);
  return {
    thread: data.thread,
    initialMessage: data.initialMessage ?? null,
  };
};

export const appendChippyMessage = async (
  threadId: string,
  payload: {
    role: ChippyMessageRole;
    text?: string;
    chart?: AssistantChartPayload | null;
    sessionState?: Record<string, unknown> | null;
  }
) => {
  const { data } = await API.post<AppendChippyMessageResponse>(`/chippy/thread/${threadId}/message`, payload);
  return {
    thread: data.thread ?? null,
    message: data.message ?? null,
  };
};

export const updateChippyMessageFeedback = async (
  threadId: string,
  messageId: string,
  payload: {
    feedback?: ChippyMessageFeedbackSentiment | null;
    feedbackComment?: string | null;
  }
) => {
  const { data } = await API.put<UpdateChippyMessageFeedbackResponse>(
    `/chippy/thread/${threadId}/message/${messageId}/feedback`,
    payload
  );
  return data.data;
};
